import { allFeatures } from '@ms-cloudpack/common-types';
import { InvalidArgumentError } from 'commander';
import didyoumean from 'didyoumean';

/**
 * Parse a `--feature` argument.
 * (This follows the signature of commander's `Option.argParser()`.)
 */
export function parseFeature(
  partialFeatureName: string,
  accFeatures: Record<string, boolean> = {},
  featureSet: Record<string, unknown> = allFeatures,
): Record<string, boolean> {
  const featureList = Object.keys(featureSet);
  // Mapping from lowercase feature name to original name
  const lowerFeatures = Object.fromEntries(featureList.map((f) => [f.toLowerCase(), f]));
  const lowerFeatureName = partialFeatureName.toLowerCase();

  if (featureSet[partialFeatureName]) {
    // Simple case: valid verbatim name
    accFeatures[partialFeatureName] = true;
  } else if (lowerFeatures[lowerFeatureName]) {
    // Case-insensitive match
    accFeatures[lowerFeatures[lowerFeatureName]] = true;
  } else {
    // Fall back to a partial match
    const matchedNames = Object.entries(lowerFeatures)
      .filter(([lowerFeat]) => lowerFeat.includes(lowerFeatureName))
      .map(([, origFeat]) => origFeat);

    if (matchedNames.length === 1) {
      // Exactly one substring match
      accFeatures[matchedNames[0]] = true;
      console.log(`Expanded --feature "${partialFeatureName}" to "${matchedNames[0]}".`);
    } else if (matchedNames.length > 1) {
      throw new InvalidArgumentError(
        `Ambiguous feature "${partialFeatureName}". Did you mean one of these? ${matchedNames.join(', ')}`,
      );
    } else {
      // If there is no exact match, suggest the closest match.
      const suggestion = didyoumean(partialFeatureName, featureList);
      throw new InvalidArgumentError(
        `Unknown feature "${partialFeatureName}".${suggestion ? ` Did you mean "${suggestion}"?` : ''}`,
      );
    }
  }

  return accFeatures;
}
